import { useEffect, useMemo, useReducer, type DragEvent, type MouseEvent, type ReactNode } from 'react';
import type { Player } from '../types.ts';
import { resolveDepthOrder } from './depth.ts';
import { shortNamer, summarise, upperFor } from './rules.ts';
import { SquadContext, type SquadContextValue } from './squadContext.ts';
import {
  boardFor,
  effectivePlayers,
  initialState,
  loadPlan,
  reducer,
  savePlan,
  type SquadState,
} from './store.ts';

function createInitialState(): SquadState {
  const base = initialState();
  const saved = loadPlan();
  return saved ? { ...base, ...saved } : base;
}

/** How long to wait after the last change before writing the plan to localStorage. */
const SAVE_DELAY_MS = 250;

export function SquadProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, createInitialState);

  useEffect(() => {
    const timer = window.setTimeout(() => savePlan(state), SAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [state]);

  const value = useMemo<SquadContextValue>(() => {
    // Everything downstream sees the squad as the user arranged it in the depth chart.
    const players = effectivePlayers(state);
    const owned = players.filter((p) => p.availability !== 'X');
    const firstTeam = players.filter((p) => p.availability === 'A');
    const shortName = shortNamer(players);

    return {
      state,
      dispatch,
      players,
      depthOrder: resolveDepthOrder(state.players, state.depthOrder),
      board: boardFor(state, state.formation),
      owned,
      firstTeam,
      listRules: summarise(firstTeam),
      squadRules: summarise(owned),
      selected: players.find((p) => p.id === state.selectedId) ?? null,
      shortName,
      posterName: (p: Player) => upperFor(p, shortName(p)),
      playerHandles: (id: string) => ({
        onClick: (event: MouseEvent) => {
          event.stopPropagation();
          dispatch({ type: 'selectPlayer', id });
        },
        draggable: true,
        onDragStart: (event: DragEvent) => {
          event.stopPropagation();
          try {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', id);
          } catch {
            // Some browsers refuse setData outside a real drag — the fallback is
            // `state.draggingId`, which the drop handler reads instead.
          }
          dispatch({ type: 'dragStart', id });
        },
        onDragEnd: () => dispatch({ type: 'dragEnd' }),
      }),
    };
  }, [state]);

  return <SquadContext.Provider value={value}>{children}</SquadContext.Provider>;
}
